import React, { useEffect, useRef, useState } from 'react';
import { Button, Card, Stack } from '@mui/material';
import Iconify from '../common/iconify.component';
import ListData from '../table-elements/list-data.component';
import {
  LISTING_COLUMNS,
  LISTING_ACTIONS,
} from '../../config/module-configs/channels.config';
import {
  getDataList,
  toggleStatus,
  getListSequence,
  updateChannelCollectionsSequence,
} from '../../services/content.service';
import {
  deleteChanelById,
  updateChannelCategoriesSequence,
} from '../../services/channel.service';
import {
  ACTIONS,
  CONTENT_ALIAS,
  LS_ADMIN_USER_ID,
} from '../../config/const.config';
import AddEditChannels from './add-edit-channels.component';
import ViewChannels from './view-channel.component';
import ConfirmPopup from '../common/confirm-popup.component';
import SnackbarInfo from '../common/snackbar-info.component';
import ImagePreview from '../content/image-preview.component';
import SearchList from '../table-elements/search-list.component';
import UploadAssets from '../content/upload-assets.component';
import ListSequenceCategories from './list-sequence-categories.component';
import ConfigBtnPopup from './config-btn-pop-up';
import {
  checkAdminUserRole,
  getLocalValue,
} from '../../utils/local-cache.util';

const ListChannels = () => {
  const columns = LISTING_COLUMNS;
  const actions = LISTING_ACTIONS;

  const searchChannelsRef = useRef(null);
  const [adminUserRole, setAdminUserRole] = useState('');
  const [isConfirmationPromptOpen, setIsConfirmationPromptOpen] =
    useState(false);
  const [options, setOptions] = useState({
    page: 0,
    rowsPerPage: 10,
    totalRows: 0,
    rows: [],
    reloadCounter: 0,
    sortBy: '',
    sortOrder: '',
    q: null,
    error: false,
    loading: true,
    searchValue: '',
  });
  const [doAction, setDoAction] = useState({
    data: null,
    action: null,
  });
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });
  const [imagePreview, setImagePreview] = useState({
    show: false,
    imageUrl: '',
    title: '',
  });
  const [action, setAction] = useState({
    show: false,
    data: '',
  });
  const [collAction, setCollAction] = useState({
    show: false,
    data: '',
  });
  const [catOptions, setCatOptions] = useState({
    error: false,
    loading: true,
  });
  const [collectionOptions, setCollectionOptions] = useState({
    error: false,
    loading: true,
  });
  const [categoriesList, setCategoriesList] = useState([]);
  const [collectionsList, setCollectionsList] = useState([]);

  const handlePageChange = (v) => {
    setOptions({
      ...options,
      page: v,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };

  const handleRowsPerPageChange = (v) => {
    setOptions({
      ...options,
      rowsPerPage: v,
      page: 0,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };

  const handleSortingChange = (sortBy, sortOrder) => {
    setOptions({
      ...options,
      sortBy,
      sortOrder,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };

  const handleRefreshData = () => {
    setOptions({
      ...options,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };

  const handleCategoriesReordering = (channelId) => {
    setAction({ ...action, show: true, data: channelId });
    let uri = 'categories-channel';
    uri += `?channelId=${channelId}`;

    getListSequence(uri)
      .then((res) => {
        setCatOptions({
          ...catOptions,
          loading: false,
          error: false,
        });
        setCategoriesList(res.data);
      })
      .catch(() =>
        setCatOptions({
          ...catOptions,
          loading: false,
          error: true,
        })
      );
  };

  const handleCollectionsReordering = (channelId) => {
    setCollAction({ ...collAction, show: true, data: channelId });
    let uri = 'collections-channel';
    uri += `?channelId=${channelId}`;

    getListSequence(uri)
      .then((res) => {
        setCollectionOptions({
          ...collectionOptions,
          loading: false,
          error: false,
        });
        setCollectionsList(res.data);
      })
      .catch(() =>
        setCollectionOptions({
          ...collectionOptions,
          loading: false,
          error: true,
        })
      );
  };

  const handleActionSelect = (selectedAction, selectedData) => {
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });
    if (selectedAction === ACTIONS.CATEGORY_SEQUENCE?.value) {
      handleCategoriesReordering(selectedData.id);
    }
    if (selectedAction === ACTIONS.COLLECTION_SEQUENCE?.value) {
      handleCollectionsReordering(selectedData.id);
    }
    setDoAction({
      ...doAction,
      data: selectedData || null,
      action: selectedAction || null,
    });
  };

  const handleActionCancel = () => {
    setDoAction({
      ...doAction,
      data: null,
      action: null,
    });

    setImagePreview({
      ...imagePreview,
      show: false,
    });
  };

  const handleActionSuccess = (message = '') => {
    if (message) {
      setSnackbarInfo({
        ...snackbarInfo,
        show: true,
        type: 'success',
        message,
      });
    }

    handleActionCancel();
    handleRefreshData();
  };

  const handleDeleteData = () => {
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });

    deleteChanelById(doAction.data.id)
      .then(() => {
        handleActionSuccess(
          `${CONTENT_ALIAS.CHANNEL_SINGULAR} deleted successfully.`
        );
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        handleActionCancel();
      });
  };

  const handleImagePreview = (rowData) => {
    setImagePreview({
      ...imagePreview,
      show: true,
      imageUrl: rowData.bannerImageUrl,
      title: rowData.title,
    });
  };

  const dataListAPICall = (newOptions = {}) => {
    const adminUserId = getLocalValue(LS_ADMIN_USER_ID);
    const getAdminRole = checkAdminUserRole();
    setAdminUserRole(getAdminRole);
    let uri = 'channels';
    uri += `?page=${newOptions?.page || options.page + 1}`;
    uri += `&perPage=${options.rowsPerPage}`;
    if (options.sortBy && options.sortOrder) {
      uri += `&sortBy=${options.sortBy}`;
      uri += `&sortOrder=${options.sortOrder}`;
    }
    uri += `&adminUserId=${adminUserId}`;
    uri += `&adminUserRole=${getAdminRole}`;
    if (options.q) {
      uri += `&q=${options.q}`;
    } else if (searchChannelsRef.current?.value) {
      uri += `&q=${searchChannelsRef.current?.value}`;
    }

    setOptions({ ...options, loading: true, searchValue: '' });
    getDataList(uri)
      .then((res) => {
        let serialRow;
        if (res?.data?.rows) {
          serialRow = res.data.rows;
          serialRow = serialRow.map((tr, count) => ({
            ...tr,
            serial: options.page * options.rowsPerPage + count + 1,
          }));
        }

        setOptions({
          ...options,
          loading: false,
          totalRows: res?.data?.totalRows || 0,
          rows: serialRow || [],
          q: null,
          error: false,
        });
      })
      .catch(() => {
        setOptions({
          ...options,
          loading: false,
          page: 0,
          totalRows: 0,
          rows: [],
          q: null,
          error: true,
        });
      });
  };

  const handleToggleStatus = (r, value, updateValueFor) => {
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });

    const payload = {
      id: r?.id,
      status: value,
      type: 'channels',
      updateValueFor,
    };

    toggleStatus(payload)
      .then(() => {
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'success',
          message: `Status updated.`,
        });

        if (payload.updateValueFor === 'status' || payload?.resetVideos) {
          handleRefreshData();
        }
      })
      .catch((e) => {
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message: e.response?.data?.message,
        });
      });
  };

  const handleSearchChange = (e) => {
    setOptions({
      ...options,
      searchValue: e.target?.value || '',
    });
  };

  const handleSearchChannels = () => {
    if (searchChannelsRef.current?.value) {
      setOptions({
        ...options,
        q: searchChannelsRef.current?.value,
        reloadCounter: options.reloadCounter + 1,
        page: 0,
      });
    }
  };

  const handleClearSearch = () => {
    setOptions({
      ...options,
      searchValue: '',
      q: null,
      reloadCounter: options.reloadCounter + 1,
    });
    searchChannelsRef.current.value = '';
  };

  useEffect(() => {
    const newOptions = options;
    newOptions.page = 0;
    setOptions({
      ...options,
      page: 0,
    });

    if (searchChannelsRef.current?.value) {
      searchChannelsRef.current.value = null;
    }
    dataListAPICall(newOptions);
  }, []);

  useEffect(() => {
    dataListAPICall();
  }, [options.reloadCounter]);

  const handleCloseDialog = (from) => {
    if (from === 'category') {
      setAction({ ...action, show: false, data: '' });
      setCatOptions({ ...catOptions, loading: true });
    } else if (from === 'collection') {
      setCollAction({ ...collAction, show: false, data: '' });
      setCollectionOptions({ ...collectionOptions, loading: true });
    }
  };

  const handleOnDragEndCategories = (result) => {
    if (result.destination) {
      const newCategories = Array.from(categoriesList);
      const [draggedItem] = newCategories.splice(result.source.index, 1);

      newCategories.splice(result.destination.index, 0, draggedItem);
      setCategoriesList(newCategories);
    }
  };

  const handleOnDragEndCollections = (result) => {
    if (result.destination) {
      const newCollections = Array.from(collectionsList);
      const [draggedItem] = newCollections.splice(result.source.index, 1);

      newCollections.splice(result.destination.index, 0, draggedItem);
      setCollectionsList(newCollections);
    }
  };

  const handleUpdateCategoriesSequence = () => {
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });

    const updatedCategoriesChannelSequence = categoriesList.map(
      (cat) => cat.category_id
    );

    const payload = {
      channelId: doAction.data.id,
      newSequence: updatedCategoriesChannelSequence,
    };

    updateChannelCategoriesSequence(payload)
      .then((res) => {
        if (res?.data?.success) {
          setSnackbarInfo({
            ...snackbarInfo,
            show: true,
            type: 'success',
            message: `${CONTENT_ALIAS.CATEGORY_PLURAL} sequence updated successfully.`,
          });
          handleCloseDialog('category');
        }
      })
      .catch(() => {
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message: 'Something went wrong.',
        });
      });
  };

  const handleUpdateCollectiosSequence = () => {
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });

    const updatedCollectionsChannelSequence = collectionsList.map(
      (col) => col.id
    );

    const payload = {
      channelId: doAction.data.id,
      newSequence: updatedCollectionsChannelSequence,
    };

    updateChannelCollectionsSequence(payload)
      .then((res) => {
        if (res?.data?.success) {
          setSnackbarInfo({
            ...snackbarInfo,
            show: true,
            type: 'success',
            message: `${CONTENT_ALIAS.COLLECTION_PLURAL} sequence updated successfully.`,
          });
          handleCloseDialog('collection');
        }
      })
      .catch(() => {
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message: 'Something went wrong.',
        });
      });
  };

  return (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{ px: 1, py: 2 }}
      >
        <div>
          {adminUserRole !== 'CONTENT_OWNER' && (
            <Button
              color="primary"
              variant="contained"
              startIcon={<Iconify icon="ic:round-add" />}
              onClick={() => handleActionSelect(ACTIONS.CREATE?.value, null)}
            >
              Create New Channel
            </Button>
          )}

          <SearchList
            label={CONTENT_ALIAS.CHANNEL_PLURAL}
            handleSearch={handleSearchChannels}
            inputRef={searchChannelsRef}
            onChange={handleSearchChange}
            options={options}
            clearSearch={handleClearSearch}
            loading={options.loading}
          />
        </div>
        <div>
          <Button
            color="inherit"
            startIcon={<Iconify icon="ic:twotone-refresh" />}
            onClick={handleRefreshData}
            disabled={options.loading}
          >
            Refresh
          </Button>
        </div>
      </Stack>

      <Card>
        <ListData
          columns={columns}
          rows={options.rows}
          page={options.page}
          rowsPerPage={options.rowsPerPage}
          totalRows={options.totalRows}
          loading={options.loading}
          actions={actions}
          error={options.error}
          sortBy={options.sortBy}
          sortOrder={options.sortOrder}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          onSortChange={handleSortingChange}
          onAction={handleActionSelect}
          handleImagePreview={handleImagePreview}
          handleToggleStatus={handleToggleStatus}
        />
      </Card>

      {doAction.action === ACTIONS.CREATE?.value && (
        <AddEditChannels
          title={`Add New ${CONTENT_ALIAS.CHANNEL_SINGULAR}`}
          dataId={0}
          onCancel={handleActionCancel}
          onSuccess={handleActionSuccess}
        />
      )}

      {doAction.action === ACTIONS.EDIT?.value && (
        <AddEditChannels
          title={`Edit ${CONTENT_ALIAS.CHANNEL_SINGULAR} Details - ${doAction.data.title}`}
          dataId={doAction.data.id}
          onCancel={handleActionCancel}
          onSuccess={handleActionSuccess}
        />
      )}
      {doAction.action === ACTIONS.VIEW?.value && (
        <ViewChannels
          title={`${CONTENT_ALIAS.CHANNEL_SINGULAR} Details - ${doAction.data.title}`}
          dataId={doAction.data.id}
          onCancel={handleActionCancel}
        />
      )}
      {doAction.action === ACTIONS.UPLOAD_BACKGROUND?.value && (
        <UploadAssets
          title={`Upload Background - ${doAction.data.title}`}
          dataId={doAction.data.id}
          dataTitle={doAction.data.title}
          onCancel={handleActionCancel}
          onSuccess={handleActionSuccess}
          from="channel"
        />
      )}
      {doAction.action === ACTIONS.CONFIG?.value && (
        <ConfigBtnPopup
          title={`Channel Config - ${doAction.data.title}`}
          dataId={doAction.data.id}
          onCancel={handleActionCancel}
          setSnackbarInfo={setSnackbarInfo}
        />
      )}

      {action.show && (
        <ListSequenceCategories
          handleOnDragEndCategories={handleOnDragEndCategories}
          handleUpdateCategoriesSequence={handleUpdateCategoriesSequence}
          action={action}
          selectedChannel={doAction.data}
          channelCategoriesList={categoriesList}
          catOptions={catOptions}
          handleCloseDialog={handleCloseDialog}
          from="category"
        />
      )}

      {collAction.show && (
        <ListSequenceCategories
          handleOnDragEndCategories={handleOnDragEndCollections}
          handleUpdateCategoriesSequence={handleUpdateCollectiosSequence}
          action={collAction}
          selectedChannel={doAction.data}
          channelCategoriesList={collectionsList}
          catOptions={collectionOptions}
          handleCloseDialog={handleCloseDialog}
          from="collection"
        />
      )}

      {doAction.action === ACTIONS.DELETE?.value && (
        <ConfirmPopup
          title={`Delete ${CONTENT_ALIAS.CHANNEL_SINGULAR}`}
          message={`Do you want to delete ${doAction.data.title} ${CONTENT_ALIAS.CHANNEL_SINGULAR}? You can not undo this action!`}
          onCancel={handleActionCancel}
          onSuccess={handleDeleteData}
        />
      )}

      {imagePreview.show && (
        <ImagePreview
          title={`Banner Image - ${imagePreview.title}`}
          onCancel={handleActionCancel}
          imageUrl={imagePreview.imageUrl}
        />
      )}

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
      {isConfirmationPromptOpen && (
        <ConfirmPopup
          title="Confirm Action"
          message="Are you sure, You want to proceed? It will remove all the videos assign to this channel"
          onCancel={() => setIsConfirmationPromptOpen(false)}
          onSuccess={() => {
            handleToggleStatus();
            setIsConfirmationPromptOpen(false);
          }}
          cancelButtonText="No"
          successButtonText="Yes"
        />
      )}
    </>
  );
};

export default ListChannels;
